import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../assets/images/white_logo.png';

const ANIMATION_DURATION = 7000; // 7s per screen

// The 3 screens' text
const SCREENS = {
  FIRST: {
    text: [
      "Hey {firstName},",
      "<b>Congratulations.</b> Very few — or perhaps none — of your peers have received this invite. Your profile was selected by one of our campus representatives from the limited number of spots allocated to them.",
      "We're <b>Prospinity</b>, and we build <b>Success Pools</b>. Success Pools are <b>index funds of talent</b> — groups of high-potential students who agree to share a small percentage of their future earnings.",
      "It's a way to <b>diversify your career</b>, <b>redistribute financial upside</b> if someone \"hits it big,\" and <b>build deep connections</b> with other exceptional students through aligned incentives."
    ]
  },
  SECOND: {
    text: [
      "We're selective about who we invite onto our platform. Our members come from top-tier universities — schools like Harvard, Yale, and Princeton — and are building ambitious ventures or heading to some of the world's most competitive firms, including Blackstone, Citadel, McKinsey, and SpaceX.",
      "This is an invitation to <b>interview with us</b> for <b>access to the Prospinity platform</b>. The interview serves two purposes:",
      "  A) To help us understand whether you're a good fit for the platform.",
      "  B) To give you a chance to decide if this is something you're genuinely interested in."
    ]
  },
  THIRD: {
    text: [
      "Joining the platform does not mean joining a Success Pool, but it gives you the opportunity to connect with similarly talented students and receive invitations to the Pools we're building — both existing and new.",
      "<b>This invitation will expire soon.</b> If you're interested, please book your interview below before your campus rep offers the spot to someone else.",
      "Feel free to read more about us in Business Insider and on our website.",
      "Any questions can be sent to applications@prospinity.com.",
      "Best,\nThe Prospinity Team"
    ]
  }
};

const InviteTypeWriter = ({ name, timeLeft, sendTrackingData }) => {
  const [isExpired, setIsExpired] = useState(false);

  // Which screen (1..3) is currently displayed
  const [currentScreen, setCurrentScreen] = useState(1);

  // Screens for which the animation is 100% completed
  const [completedScreens, setCompletedScreens] = useState(new Set());

  // Whether all screens (1..3) have finished their animations
  const [allScreensLoaded, setAllScreensLoaded] = useState(false);

  // Current screen's progress from 0% → 100%
  const [progress, setProgress] = useState(0);

  // Basic auto-advance: each screen gets 7s to animate from 0→100
  useEffect(() => {
    // If the current screen is already completed or everything loaded or expired, skip re-animation
    if (isExpired || completedScreens.has(currentScreen) || allScreensLoaded) {
      setProgress(100);
      return;
    }

    let startTime = Date.now();
    let frameId;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const fraction = elapsed / ANIMATION_DURATION;
      const newProgress = Math.min(100, fraction * 100);
      setProgress(newProgress);

      if (newProgress < 100) {
        frameId = requestAnimationFrame(animate);
      } else {
        // Mark this screen as finished
        setCompletedScreens((prev) => new Set([...prev, currentScreen]));

        // If not the last screen, auto-advance
        if (currentScreen < 3) {
          setCurrentScreen(currentScreen + 1);
          setProgress(0);
        } else {
          setAllScreensLoaded(true);
        }
      }
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [currentScreen, isExpired, allScreensLoaded, completedScreens]);

  // Format time as HH:MM:SS
  const formatTime = (seconds) => {
    if (seconds <= 0) return '00:00:00';
    const hh = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const mm = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const ss = String(seconds % 60).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };

  // Replace "Business Insider" & "website" with clickable spans
  const replaceLinks = (text) =>
    text
      .replace(
        'Business Insider',
        `<span class="custom-link" onclick="window.open('https://www.businessinsider.com/prospinity-income-sharing-raise-2-million-slow-ventures-2024-12', '_blank')">Business Insider</span>`
      )
      .replace(
        'website',
        `<span class="custom-link" onclick="window.open('https://prospinity.com', '_blank')">our website</span>`
      );

  // Track expiration
  useEffect(() => {
    if (isExpired) {
      sendTrackingData('expired invitation');
    }
  }, [isExpired, sendTrackingData]);

  // Jump to a screen, but only if it is already completed (or everything is loaded) or it is behind our current progress
  const handleScreenChange = (target) => {
    if (
      target >= 1 && target <= 3 &&
      (allScreensLoaded ||
       completedScreens.has(target) ||
       target <= Math.max(...completedScreens, currentScreen))
    ) {
      setCurrentScreen(target);
      setProgress(
        (allScreensLoaded || completedScreens.has(target)) ? 100 : 0
      );
    }
  };

  // Render paragraphs for the current screen
  const renderScreenText = () => {
    const screenData =
      currentScreen === 1
        ? SCREENS.FIRST
        : currentScreen === 2
        ? SCREENS.SECOND
        : SCREENS.THIRD;

    return screenData.text.map((p, idx) => {
      const replaced = p.replace('{firstName}', name.split(' ')[0]);
      return (
        <motion.p
          key={idx}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: idx * 0.1 }}
          style={styles.text}
          dangerouslySetInnerHTML={{ __html: replaceLinks(replaced) }}
        />
      );
    });
  };

  /* Update the button visibility logic */
  // Show back button when we're not on first screen and previous screen is loaded
  const showBack = currentScreen > 1;

  // Show next button when current screen is loaded and we're not on last screen
  const showNext = (completedScreens.has(currentScreen) || allScreensLoaded) && currentScreen < 3;

  // Handler for Back button
  const handleGoBack = () => {
    if (currentScreen > 1) {
      const prev = currentScreen - 1;
      setCurrentScreen(prev);
      // if that screen is fully loaded, skip re-animating
      setProgress((completedScreens.has(prev) || allScreensLoaded) ? 100 : 0);
    }
  };

  // Handler for Next button
  const handleGoNext = () => {
    if (currentScreen < 3) {
      const nxt = currentScreen + 1;
      setCurrentScreen(nxt);
      // Reset progress for next screen if not completed
      setProgress(completedScreens.has(nxt) || allScreensLoaded ? 100 : 0);
    }
  };

  // If invitation is expired, show that layout
  if (isExpired) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={styles.container}>
        <div style={styles.inner}>
          <motion.div style={styles.textBox}>
            <img src={logo} alt="Prospinity Logo" style={styles.logo} />
            <div style={styles.headerRow}>
              <h1 style={styles.heading}>Sorry, your invitation is expired</h1>
            </div>
            <div style={styles.contentContainer}>
              <button
                style={{
                  ...styles.calendlyButton,
                  marginTop: '20px',
                  backgroundColor: 'rgba(255, 78, 78, 0.1)',
                  border: '1px solid rgba(255, 78, 78, 0.3)',
                }}
                onClick={() => {
                  window.open(
                    'mailto:applications@prospinity.com?subject=Request%20New%20Invite&body=My%20invite%20expired.%20Would%20that%20be%20possible%20to%20receive%20another%20one%3F%20Thanks!',
                    '_blank'
                  );
                }}
              >
                REQUEST EXTENSION
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={styles.container}>
      <div style={styles.inner}>
        {/* Header with logo and timer */}
        <div style={styles.headerRow}>
          <img src={logo} alt="Prospinity Logo" style={styles.logo} />
          <div style={styles.expiryTimer}>Expires in: {formatTime(timeLeft)}</div>
        </div>

        <motion.div style={styles.mainBox}>
          <div style={styles.contentContainer}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentScreen}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={styles.textContent}
              >
                {/* Navigation buttons at the top of content */}
                {(showBack || showNext) && (
                  <div style={styles.buttonsRow}>
                    {showBack && (
                      <motion.button
                        style={{ ...styles.navButton, marginRight: 'auto' }}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleGoBack}
                      >
                        &larr; Back
                      </motion.button>
                    )}
                    {showNext && (
                      <motion.button
                        style={{ ...styles.navButton, marginLeft: showBack ? '8px' : 'auto' }}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleGoNext}
                      >
                        Next &rarr;
                      </motion.button>
                    )}
                  </div>
                )}

                {/* Text content with margin when buttons are present */}
                <div style={{ 
                  marginTop: (showBack || showNext) ? '12px' : '0'
                }}>
                  {renderScreenText()}
                </div>

                {/* Book Interview button on last screen */}
                {currentScreen === 3 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    style={styles.buttonWrapper}
                  >
                    <motion.button
                      style={styles.calendlyButton}
                      onClick={() => {
                        window.Calendly.initPopupWidget({
                          url: 'https://calendly.com/d/cqzk-hdp-xmc/prospinity-membership-interview-invitation-only',
                        });
                        sendTrackingData('interview button');
                      }}
                      whileHover={{
                        scale: 1.02,
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span style={styles.buttonText}>Book Interview</span>
                    </motion.button>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Progress bars */}
          <div style={styles.progressContainer}>
            {[1, 2, 3].map((screen) => (
              <div
                key={screen}
                style={styles.progressBarTrack}
                onClick={() => handleScreenChange(screen)}
              >
                <motion.div
                  style={{
                    ...styles.progressBarFill,
                    width:
                      allScreensLoaded || completedScreens.has(screen)
                        ? '100%'
                        : screen === currentScreen
                        ? `${progress}%`
                        : '0%'
                  }}
                  transition={{ duration: 0.1 }}
                />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

/* ================ STYLES ================ */
const styles = {
  container: {
    color: '#fff',
    minHeight: '100vh',
    margin: 0,
    padding: '16px',
    fontFamily: `'Space Grotesk', sans-serif`,
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: '#111827',
    overflow: 'hidden'
  },
  inner: {
    width: '100%',
    maxWidth: '800px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    height: 'fit-content'
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  logo: {
    width: '110px'
  },
  expiryTimer: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#ff9f43',
    padding: '4px 8px',
    backgroundColor: 'rgba(255, 159, 67, 0.1)',
    borderRadius: '6px',
    border: '1px solid rgba(255, 159, 67, 0.3)'
  },
  mainBox: {
    backgroundColor: 'rgba(0,0,0,0.4)',
    padding: '20px',
    borderRadius: '16px',
    boxShadow: '0 0 20px rgba(78, 166, 255, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    height: 'fit-content'
  },
  buttonsRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%'
  },
  navButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
    textTransform: 'uppercase',
    fontWeight: '500'
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '16px',
    height: 'fit-content'
  },
  textContent: {
    display: 'flex',
    flexDirection: 'column',
    height: 'fit-content'
  },
  text: {
    fontSize: '16px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    marginBottom: '10px',
    textAlign: 'left'
  },
  buttonWrapper: {
    marginTop: '12px',
    width: '100%'
  },
  calendlyButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#ffffff',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '13px',
    fontWeight: '500',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 0 20px rgba(255, 255, 255, 0.1)',
    width: '100%',
    position: 'relative',
    overflow: 'hidden'
  },
  buttonText: {
    position: 'relative',
    zIndex: 2
  },
  progressContainer: {
    display: 'flex',
    gap: '6px',
    marginTop: '4px',
    height: '4px'
  },
  progressBarTrack: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '2px',
    overflow: 'hidden',
    cursor: 'pointer'
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#4ea6ff',
    borderRadius: '2px'
  },
  // Only used for the expired layout
  textBox: {},
  heading: { fontSize: '20px' }
};

// Insert custom link styling for <span class="custom-link">
const styleTag = document.createElement('style');
styleTag.innerHTML = `
  .custom-link {
    text-decoration: underline;
    color: #4ea6ff;
    cursor: pointer;
  }
`;
document.head.appendChild(styleTag);

export default InviteTypeWriter;
